// Default texts of the search results page, per language (iteration 26). defineResults
// (../define.ts) fills every key below the config leaves `undefined` from the pack of the config's
// `locale` (./locale.ts textLanguage) — so this module ships only in bundles whose config defines
// results (or the modal, whose pack extends this one: ./modal.ts). Pure data + a pure merge.
//
// Sources:
//   - cs, sk: the customers' configs (most common value per key and language); `sortByTitle`
//     from rcobchod-cz (cs), sk drafted.
//   - en, de: drafted (no customer runs results in English or German yet); de in the formal
//     register.
//   - `noResultsFoundMessage`: shared with the dropdown and modal (./shared.ts, sources there).
//
// Text keys of the results config deliberately WITHOUT a default (the config sets them itself):
//   - `title` (the heading before the query): leaving it out is how a config drops the heading,
//     and an override couldn't bring that back (`""` still renders the heading with the query).
//   - `searchingForMessage` / `wouldYouLikeToSearchForMessage`: setting them switches on the
//     "searching for X, would you like to search for Y?" hint (most results configs leave it off).
//   - `readMoreTitle`: setting it switches on the "read more" links of the additional / promo
//     content.
//   - `inStockText`, `defaultButtonLabel`: per-shop labels, not UI language texts.
//   - `sortOptions[].name`, `additionalContent[].title`, `promoContent[].title`: per-shop.

import type { ResultsLocalizationConfig } from "@raventic/sdk-search-v1";
import { fillTexts, textLanguage } from "./locale.ts";
import type { TextLanguage, TextPacks } from "./locale.ts";
import { NO_RESULTS_MESSAGES } from "./shared.ts";

export type ResultsTextKey =
  | "noResultsFoundMessage"
  | "categoriesTitle"
  | "manufacturersTitle"
  | "priceTitle"
  | "noQueryTitle"
  | "filtersTitle"
  | "filterButtonTitle"
  | "clearAllFiltersTitle"
  | "productsTitle"
  | "sortByTitle";
export type ResultsTexts = Required<Pick<ResultsLocalizationConfig, ResultsTextKey>>;

// The results-only texts; the shared no-results message comes from ./shared.ts. Plain literals:
// the module stays tree-shakable (see dropdownTexts in ./dropdown.ts).
const RESULTS_OWN_TEXTS = {
  cs: {
    categoriesTitle: "Kategorie",
    manufacturersTitle: "Výrobce",
    priceTitle: "Cena",
    noQueryTitle: "Produkty",
    filtersTitle: "Filtry",
    filterButtonTitle: "Zobrazit filtry",
    clearAllFiltersTitle: "Zrušit filtry",
    productsTitle: "Produkty",
    sortByTitle: "Řadit podle",
  },
  sk: {
    categoriesTitle: "Kategórie",
    manufacturersTitle: "Výrobca",
    priceTitle: "Cena",
    noQueryTitle: "Produkty",
    filtersTitle: "Filtre",
    filterButtonTitle: "Zobraziť filtre",
    clearAllFiltersTitle: "Zrušiť filtre",
    productsTitle: "Produkty",
    sortByTitle: "Zoradiť podľa",
  },
  en: {
    categoriesTitle: "Categories",
    manufacturersTitle: "Manufacturers",
    priceTitle: "Price",
    noQueryTitle: "Products",
    filtersTitle: "Filters",
    filterButtonTitle: "Show filters",
    clearAllFiltersTitle: "Clear filters",
    productsTitle: "Products",
    sortByTitle: "Sort by",
  },
  de: {
    categoriesTitle: "Kategorien",
    manufacturersTitle: "Hersteller",
    priceTitle: "Preis",
    noQueryTitle: "Produkte",
    filtersTitle: "Filter",
    filterButtonTitle: "Filter anzeigen",
    clearAllFiltersTitle: "Filter zurücksetzen",
    productsTitle: "Produkte",
    sortByTitle: "Sortieren nach",
  },
} as const satisfies TextPacks<Omit<ResultsTexts, "noResultsFoundMessage">>;

// The results' whole pack for `language` (built per call, like dropdownTexts).
export function resultsTexts(language: TextLanguage): ResultsTexts {
  return { noResultsFoundMessage: NO_RESULTS_MESSAGES[language], ...RESULTS_OWN_TEXTS[language] };
}

// A results config with its missing texts filled (see the header). Never mutates; a config that
// already sets every text is returned as-is.
export function withResultsTexts<C extends object>(config: C): C {
  return fillTexts(config, resultsTexts(textLanguage((config as { locale?: unknown }).locale)));
}
